import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchFooter, type Footer } from '../../services/footer';
import { fetchNavigation } from '../../services/navigation';
import { fetchContactPage, type ContactPage } from '../../services/contactPage';

// Same artwork family as the hero: dark navy with diagonal streaks + dot grids baked in.
// AI-upscaled source encoded as WebP q92 (67KB) - WebP keeps the smooth dark gradients clean,
// where JPEG showed visible banding. Rendered at 40% opacity over the navy base colour so the
// streaks stay a subtle texture instead of competing with the content.
const FOOTER_BG = '/images/footer/footer-bg.webp';

// Complementary description under the tagline. Deliberately NOT the hero description - the
// footer should not repeat the hero copy word for word (Gerald's request).
const FOOTER_DESCRIPTION =
  'Ganzheitliche Transformation für Business und Leben - wo strategische Klarheit auf persönliche Entwicklung trifft.';

// Icons for the contact rows, in render order: phone, email, address.
const CONTACT_ICONS = ['ri-phone-line', 'ri-mail-line', 'ri-map-pin-line'];

export default function Footer() {
  const [footer, setFooter] = useState<Footer | null>(null);
  const [contact, setContact] = useState<ContactPage | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const loadData = async () => {
      try {
        const [footerData, navData, contactData] = await Promise.all([
          fetchFooter(),
          fetchNavigation(),
          fetchContactPage(),
        ]);
        setFooter(footerData);
        setContact(contactData);
        if (navData?.logoUrl) {
          setLogoUrl(navData.logoUrl);
        }
      } catch (error) {
        console.error('Failed to load footer:', error);
      }
    };
    loadData();
  }, []);

  const tagline = (footer?.brandDescription || 'Innen klar. Außen wirksam.').trim();
  const sections = footer?.sections || [];
  const copyrightText = footer?.copyrightText || `© ${currentYear} Gerald Fasching. Alle Rechte vorbehalten.`;

  const contactRows = [
    contact?.phone && { icon: CONTACT_ICONS[0], text: contact.phone, href: `tel:${contact.phone.replace(/\s/g, '')}` },
    contact?.email && { icon: CONTACT_ICONS[1], text: contact.email, href: `mailto:${contact.email}` },
    contact?.address && { icon: CONTACT_ICONS[2], text: contact.address, href: null },
  ].filter(Boolean) as { icon: string; text: string; href: string | null }[];

  // Gold uppercase column title + short gold underline (matches the reference design)
  const columnTitle = (title: string) => (
    <div className="mb-6">
      <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] lg:text-[13px]">{title}</h3>
      <div aria-hidden className="mt-3 h-px w-10 bg-[#C9A961]/60"></div>
    </div>
  );

  return (
    <footer className="relative isolate overflow-hidden bg-[#0F2347] text-white">
      {/* Artwork background (same visual family as the hero blue) */}
      <img src={FOOTER_BG} alt="" aria-hidden loading="lazy" decoding="async" className="absolute inset-0 -z-10 h-full w-full object-cover opacity-40" />

      <div className="mx-auto max-w-7xl px-6 py-16 lg:px-12 lg:py-20">
        <div className="mb-12 grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-[1.3fr_1fr_1fr_1.2fr] lg:gap-10">
          {/* Brand Column */}
          <div>
            <div className="flex items-center space-x-3">
              {logoUrl ? (
                <img src={logoUrl} alt="Gerald Fasching Logo" className="h-12 w-auto" />
              ) : (
                <div className="flex h-10 w-10 items-center justify-center">
                  <i className={`${footer?.brandIcon || 'ri-fire-line'} text-3xl text-[#D4AF37]`}></i>
                </div>
              )}
              <span className="font-serif text-xl font-bold text-white" style={{ fontFamily: 'Lora, serif' }}>
                {footer?.brandName || 'Gerald Fasching'}
              </span>
            </div>
            <div aria-hidden className="my-5 h-px w-10 bg-[#C9A961]/60"></div>
            <p className="font-serif text-base font-semibold italic text-white lg:text-lg" style={{ fontFamily: 'Lora, serif' }}>
              {tagline}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-white/60 lg:text-base">
              {FOOTER_DESCRIPTION}
            </p>
          </div>

          {/* Link Sections (Navigation, Quick Links) */}
          {sections.map((section, index) => (
            <div key={index} className="lg:border-l lg:border-white/10 lg:pl-10">
              {columnTitle(section.title)}
              <ul className="space-y-3">
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <Link
                      to={link.path}
                      className="group inline-flex cursor-pointer items-center gap-2 text-sm text-white/85 transition-colors hover:text-[#D4AF37] lg:text-base"
                    >
                      <i aria-hidden className="ri-arrow-right-s-line text-base leading-none text-[#C9A961] transition-transform group-hover:translate-x-0.5"></i>
                      {link.text}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact Column - deliberately utilitarian ("Direkt erreichbar", no pitch line):
               the final CTA section directly above already asks for the conversation, so the
               footer just provides the plain contact facts instead of asking a second time. */}
          {contactRows.length > 0 && (
            <div className="lg:border-l lg:border-white/10 lg:pl-10">
              {columnTitle('Direkt erreichbar')}
              <ul className="space-y-4">
                {contactRows.map((row, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <i aria-hidden className={`${row.icon} mt-0.5 text-lg leading-none text-[#C9A961]`}></i>
                    {row.href ? (
                      <a href={row.href} className="cursor-pointer text-sm text-white/85 transition-colors hover:text-[#D4AF37] lg:text-base">
                        {row.text}
                      </a>
                    ) : (
                      <span className="text-sm text-white/85 lg:text-base">{row.text}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Bottom Bar: copyright | pillars | legal */}
        <div className="border-t border-white/10 pt-8">
          <div className="grid grid-cols-1 items-center gap-4 text-center md:grid-cols-3 md:text-left">
            <p className="text-xs text-white/50 lg:text-sm">
              {copyrightText.replace('{year}', currentYear.toString())}
            </p>
            <p aria-hidden className="text-xs font-bold uppercase tracking-[0.3em] text-[#C9A961] md:text-center lg:text-[13px]">
              Business&ensp;·&ensp;Human&ensp;·&ensp;Spirit
            </p>
            <div className="flex justify-center gap-6 text-xs text-white/50 md:justify-end lg:text-sm">
              {footer?.legalLinks && footer.legalLinks.length > 0 ? (
                footer.legalLinks.map((link, index) => (
                  <Link key={index} to={link.path} className="cursor-pointer transition-colors hover:text-[#D4AF37]">
                    {link.text}
                  </Link>
                ))
              ) : (
                <>
                  <Link to="/datenschutz" className="cursor-pointer transition-colors hover:text-[#D4AF37]">
                    Datenschutz
                  </Link>
                  <Link to="/impressum" className="cursor-pointer transition-colors hover:text-[#D4AF37]">
                    Impressum
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
